import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Typography,
  Container,
  CircularProgress,
  IconButton,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import { myDesignService } from "../../services";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import TheatersIcon from "@mui/icons-material/Theaters";
import PreviewPlayer from "../previewPlayer/PreviewPlayer";

const PREFIX = "CampaignUnusedContent";

const classes = {
  rootContainer: `${PREFIX}-rootContainer`,
  playlistsContainer: `${PREFIX}-playlistsContainer`,
  playlistContainer: `${PREFIX}-playlistContainer`,
  loaderContainer: `${PREFIX}-loaderContainer`,
  elementContainer: `${PREFIX}-elementContainer`,
  playlistInformations: `${PREFIX}-playlistInformations`,
  marginTop: `${PREFIX}-marginTop`,
  togglePosition: `${PREFIX}-togglePosition`,
  toggleVisuPosition: `${PREFIX}-toggleVisuPosition`,
  contentInfosContainer: `${PREFIX}-contentInfosContainer`,
  description: `${PREFIX}-description`,
  wordBreakStyle: `${PREFIX}-wordBreakStyle`,
  typeIcon: `${PREFIX}-typeIcon`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.rootContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "50%",
    position: "relative",
    left: "50%",
    transform: "translateX(-50%)",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
    },
  },

  [`& .${classes.playlistsContainer}`]: {
    marginTop: "1rem",
    [theme.breakpoints.up("sm")]: {
      margin: "0 auto",
      marginTop: "1rem",
    },
  },

  [`& .${classes.playlistContainer}`]: {
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    [theme.breakpoints.up("sm")]: {
      width: "350px",
    },
    height: "550px",
    [theme.breakpoints.between("xs", "md")]: {
      height: "400px",
    },
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
    marginTop: "0.5rem",
    padding: "1px",
  },

  [`& .${classes.loaderContainer}`]: {
    position: "relative",
    left: "50%",
    transform: "translateX(-50%)",
    width: "20%",
    marginTop: "2rem",
  },

  [`& .${classes.elementContainer}`]: {
    minHeight: "80px",
    maxWidth: "350px",
    marginBottom: "1rem",
    display: "flex",
    padding: "0 1rem",
    position: "relative",
    left: "50%",
    transform: "translateX(-50%)",
  },

  [`& .${classes.playlistInformations}`]: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    margin: "0.5rem 0 1.5rem 0",
    width: "100%",
    height: "80%",
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "0.5rem",
  },

  [`& .${classes.togglePosition}`]: {
    position: "absolute",
    right: "0",
  },

  [`& .${classes.toggleVisuPosition}`]: {
    position: "absolute",
    right: 0,
    bottom: 0,
    zIndex: 999,
  },

  [`& .${classes.contentInfosContainer}`]: {
    display: "flex",
    flexDirection: "row",
    paddingTop: "0.25rem",
    height: "50%",
    width: "80%",
  },

  [`& .${classes.description}`]: {
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "90%",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },

  [`& .${classes.wordBreakStyle}`]: {
    marginLeft: "1rem",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    color: "#D3AF5F",
  },

  [`& .${classes.typeIcon}`]: {
    transform: "translateY(4px)",
  },
}));

export default function CampaignUnusedContent(props) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [media, setMedia] = useState(null);
  const loading = useSelector((state) => state.myDesigns.selectedZone.loading);
  const disableAction = useSelector(
    (state) => state.myDesigns.selectedZone.disableAction
  );
  const toastError = () =>
    enqueueSnackbar(t("toasts.errorAddCampaignElement"), { variant: "error" });

  const MyContentElement = (props) => {
    const name =
      props.item.lastName && props.item.firstName
        ? props.item.lastName + " " + props.item.firstName
        : props.item.lastname + " " + props.item.firstname;
    return (
      <Paper
        elevation={12}
        className={classes.elementContainer}
        key={`element-playlist-${props.index}`}
      >
        <div className={classes.playlistInformations}>
          <div className={classes.contentInfosContainer}>
            <TheatersIcon className={classes.typeIcon} />
            <Typography variant="h5" className={classes.wordBreakStyle}>
              {name}
            </Typography>
          </div>
          <IconButton
            onClick={() =>
              setMedia({
                content: props.item.video,
                title: props.item.email,
                type: "video",
              })
            }
            className={classes.toggleVisuPosition}
            color="primary"
            aria-label="preview content"
            component="span"
            style={{
              padding: 0,
            }}
            size="large"
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              myDesignService
                .addElementToCampaignPlaylist(
                  props.selectedCampaign.campaignId,
                  props.selectedCampaign.id,
                  props.item.id,
                  toastError
                )
                .then((res) => {
                  let tmpContent = [...props.content];
                  tmpContent.splice(
                    props.content.findIndex(
                      (item) => item.id === props.item.id
                    ),
                    1
                  );
                  props.setContent(tmpContent);
                  props.setPlaylist(res);
                });
            }}
            disabled={disableAction}
            className={classes.togglePosition}
            color="primary"
            aria-label="add element to playlist"
            component="span"
            style={{
              padding: 0,
            }}
            size="large"
          >
            <AddIcon />
          </IconButton>
          {props.item.email && (
            <Typography variant="body1" className={classes.description}>
              {props.item.email}
            </Typography>
          )}
        </div>
      </Paper>
    );
  };

  if (
    (loading && !props.content) ||
    !props.content ||
    !props.selectedCampaign
  ) {
    return (
      <Container>
        <Root>
          <div className={classes.loaderContainer}>
            <CircularProgress size={50} />
          </div>
        </Root>
      </Container>
    );
  } else {
    return (
      <Root>
        <div className={classes.rootContainer}>
          <ResponsiveDialogManager
            title={media && media.title}
            fullScreen
            open={media ? true : false}
            onClose={() => {
              setMedia(null);
            }}
            renderer={<PreviewPlayer media={media && media} />}
          />
          <div className={classes.playlistsContainer}>
            <div
              className={classes.playlistContainer}
              style={{ height: "75vh" }}
            >
              {props.content.length === 0 ? (
                <Typography variant="body1" className={classes.marginTop}>
                  {t("myDesign.zones.playlists.noResults")}
                </Typography>
              ) : (
                props.content.map((item, indexElem) => (
                  <MyContentElement
                    key={`content-${indexElem + 1}`}
                    item={item}
                    selectedCampaign={props.selectedCampaign}
                    content={props.content}
                    setContent={props.setContent}
                    playlist={props.playlist}
                    setPlaylist={props.setPlaylist}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </Root>
    );
  }
}
